import { useEffect, useRef } from "react";
import { cn } from "@/utils/cn";

export interface TableSelectionControlProps {
  pageSelectableCount: number;
  allOnPageSelected: boolean;
  someOnPageSelected: boolean;
  onTogglePage: () => void;
  /** Select all rows from pages opened in this search session. */
  onSelectAllPages?: () => void;
  allPagesDisabled?: boolean;
  allPagesActive?: boolean;
  loadedPagesSelectableCount?: number;
  totalSelectedCount?: number;
  onClearSelection?: () => void;
  allPagesTitle?: string;
}

export function TableSelectionControl({
  pageSelectableCount,
  allOnPageSelected,
  someOnPageSelected,
  onTogglePage,
  onSelectAllPages,
  allPagesDisabled = false,
  allPagesActive = false,
  loadedPagesSelectableCount = 0,
  totalSelectedCount = 0,
  onClearSelection,
  allPagesTitle,
}: TableSelectionControlProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = checkboxRef.current;
    if (!el) return;
    el.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  return (
    <div className="table-selection-control">
      <div
        className="table-selection-segment"
        role="group"
        aria-label="Selection scope"
      >
        <label
          className={cn(
            "table-selection-segment__item",
            allOnPageSelected && "is-active",
          )}
        >
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={allOnPageSelected}
            disabled={pageSelectableCount === 0}
            onChange={onTogglePage}
            aria-label={`Select this page (${pageSelectableCount} rows)`}
          />
          <span>This page</span>
          {pageSelectableCount > 0 ? (
            <span className="table-selection-segment__count tabular-nums">
              ({pageSelectableCount})
            </span>
          ) : null}
        </label>

        {onSelectAllPages ? (
          <button
            type="button"
            className={cn(
              "table-selection-segment__item",
              allPagesActive && "is-active",
            )}
            disabled={allPagesDisabled || loadedPagesSelectableCount === 0}
            title={allPagesTitle}
            onClick={onSelectAllPages}
            aria-pressed={allPagesActive}
          >
            All pages
            {loadedPagesSelectableCount > 0 ? (
              <span className="table-selection-segment__count tabular-nums">
                ({loadedPagesSelectableCount})
              </span>
            ) : null}
          </button>
        ) : null}
      </div>

      {totalSelectedCount > 0 && onClearSelection ? (
        <button
          type="button"
          className="table-selection-clear link-quiet"
          onClick={onClearSelection}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}
